import React from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getNumberComma } from '../../../utils/numberUtils';
import {
  bodyCellSx,
  buildCostColPercents,
  COST_TABLE_MIN_WIDTH,
  headerCellSx,
  lineClamp2Sx,
  numericCellSx,
  profitRateColor,
} from './costCalcShared';

const COST_COL_PERCENTS = buildCostColPercents();

// 옵션 B(owner 승인): 원가 산출 내역의 보기전용 표. 편집 가능 상태(CostCalcListForm, 행
// 클릭 -> 우측 패널)와 확정된 상태(CostCalcLockList)가 완전히 같은 표를 그린다 - 입력칸은
// 셀 안에 전혀 없고(수정은 패널에서만), editable일 때만 행 클릭/선택 표시가 붙는다.
const CostCalcTable = ({ rows, totals, editable = false, selectedIndex = null, onRowClick }) => {
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: COST_TABLE_MIN_WIDTH, width: '100%', tableLayout: 'fixed' }}>
        {/* colgroup으로 열 너비를 %로 직접 지정 - 패널이 열리고 닫히며 컨테이너 폭이 바뀌는
            이 화면에서는 %가 px보다 안정적이다(costCalcShared의 buildCostColPercents 참고). */}
        <colgroup>
          {COST_COL_PERCENTS.map((pct, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <col key={i} style={{ width: pct }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={headerCellSx}>No</TableCell>
            <TableCell sx={headerCellSx}>품목</TableCell>
            <TableCell align="right" sx={headerCellSx}>수량</TableCell>
            <TableCell align="right" sx={headerCellSx}>매출 단가</TableCell>
            <TableCell align="right" sx={headerCellSx}>매출 금액</TableCell>
            <TableCell align="right" sx={headerCellSx}>매입 단가</TableCell>
            <TableCell align="right" sx={headerCellSx}>매입 금액</TableCell>
            <TableCell sx={headerCellSx}>매입처</TableCell>
            <TableCell align="right" sx={headerCellSx}>이익</TableCell>
            <TableCell align="right" sx={headerCellSx}>이익률</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => {
            const selected = editable && selectedIndex === idx;
            return (
              <TableRow
                key={row.key}
                hover={editable}
                onClick={editable && onRowClick ? () => onRowClick(idx) : undefined}
                sx={{
                  cursor: editable ? 'pointer' : 'default',
                  ...(selected && {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.primary.main}`,
                  }),
                }}
              >
                <TableCell align="center" sx={bodyCellSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    {row.invalid && (
                      <Tooltip title="입력값을 확인하세요" arrow>
                        <Box
                          component="span"
                          aria-label="입력 오류"
                          sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }}
                        />
                      </Tooltip>
                    )}
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {row.no}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500} sx={{ ...lineClamp2Sx, minWidth: 0 }}>
                        {row.productNm || '(품목명 없음)'}
                      </Typography>
                      {row.isEtc && (
                        <Chip
                          label="기타"
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, mt: 0.25, fontSize: '0.6875rem', flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}
                    </Box>
                    {row.productDesc && (
                      <Typography variant="caption" color="text.secondary" component="div" noWrap>
                        {row.productDesc}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                <TableCell sx={numericCellSx}>
                  {getNumberComma(row.qty)}
                  {row.unit ? ` ${row.unit}` : ''}
                </TableCell>
                <TableCell sx={numericCellSx}>{getNumberComma(row.sellPrice)}</TableCell>
                <TableCell sx={numericCellSx}>{getNumberComma(row.sellAmount)}</TableCell>
                <TableCell sx={numericCellSx}>{getNumberComma(row.buyPrice)}</TableCell>
                <TableCell sx={numericCellSx}>{getNumberComma(row.buyAmount)}</TableCell>

                <TableCell sx={bodyCellSx}>
                  {row.missingPartner ? (
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      매입처 필요
                    </Typography>
                  ) : (
                    <Tooltip title={row.partnerNm || ''} arrow>
                      <Typography variant="body2" noWrap>
                        {row.partnerNm}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell sx={numericCellSx}>{getNumberComma(row.profit)}</TableCell>
                <TableCell sx={{ ...numericCellSx, color: profitRateColor(row.profitRate), fontWeight: 700 }}>
                  {getNumberComma(row.profitRate)}%
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        {totals && (
          <TableFooter>
            <TableRow
              sx={{
                '& .MuiTableCell-root': { borderTop: 2, borderColor: 'divider', fontWeight: 700, bgcolor: 'grey.50' },
              }}
            >
              <TableCell sx={bodyCellSx} />
              <TableCell sx={bodyCellSx}>합계</TableCell>
              <TableCell sx={bodyCellSx} />
              <TableCell sx={bodyCellSx} />
              <TableCell sx={numericCellSx}>{`₩${getNumberComma(totals.totalSales)}`}</TableCell>
              <TableCell sx={bodyCellSx} />
              <TableCell sx={numericCellSx}>{`₩${getNumberComma(totals.totalPurchase)}`}</TableCell>
              <TableCell sx={bodyCellSx} />
              <TableCell sx={numericCellSx}>{`₩${getNumberComma(totals.totalProfit)}`}</TableCell>
              <TableCell sx={{ ...numericCellSx, color: profitRateColor(totals.totalMargin) }}>
                {getNumberComma(totals.totalMargin)}%
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};

export default CostCalcTable;
